import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mic, Play, Pause, Loader2, Check, Volume2 } from "lucide-react";
import api from "../lib/api";

/**
 * Iter-27.40 — Configuration de la voix du formateur Shariti (ElevenLabs).
 *
 * Permet à l'admin de :
 *  - Lister les voix françaises disponibles dans la library publique ElevenLabs
 *  - Écouter un échantillon AVANT activation (preview à la volée)
 *  - Activer une voix → bascule instantanée côté apprenant (cache TTS clé/voice_id)
 *
 * UX : carte par voix avec name, descriptive, use_case, accent, gender, bouton Preview,
 * bouton Activer. La voix active est highlight en orange.
 */
export default function TtsVoiceSettings() {
    const [voices, setVoices] = useState([]);
    const [activeId, setActiveId] = useState("");
    const [loading, setLoading] = useState(true);
    const [previewing, setPreviewing] = useState(null); // voice_id en cours de preview
    const [playing, setPlaying] = useState(null); // voice_id en cours de lecture
    const [activating, setActivating] = useState(null);
    const [filter, setFilter] = useState("all"); // all | male | female | educational
    const audioRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/tts/voices");
            setVoices(data.voices || []);
            setActiveId(data.active_voice_id || "");
        } catch (e) {
            toast.error("Impossible de charger les voix ElevenLabs — clé API configurée ?");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        return () => {
            if (audioRef.current) audioRef.current.pause();
        };
    }, []);

    const handlePreview = async (voiceId) => {
        // Si on est déjà en train de lire cette voix, on pause/stoppe
        if (playing === voiceId && audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
            setPlaying(null);
            return;
        }
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
            setPlaying(null);
        }
        setPreviewing(voiceId);
        try {
            const form = new FormData();
            form.append("voice_id", voiceId);
            const res = await api.post("/admin/tts/preview", form, {
                responseType: "blob",
                headers: { "Content-Type": "multipart/form-data" },
            });
            const url = URL.createObjectURL(res.data);
            const audio = new Audio(url);
            audio.onended = () => setPlaying(null);
            audio.onpause = () => {
                if (audio.currentTime >= audio.duration - 0.1) setPlaying(null);
            };
            audioRef.current = audio;
            await audio.play();
            setPlaying(voiceId);
        } catch (e) {
            toast.error("Génération du sample impossible (quota ElevenLabs ?)");
        } finally {
            setPreviewing(null);
        }
    };

    const handleActivate = async (voiceId, voiceName) => {
        setActivating(voiceId);
        try {
            await api.put("/admin/tts/voice", { lesson_voice_id: voiceId });
            setActiveId(voiceId);
            toast.success(`Voix "${voiceName}" activée pour tous les apprenants.`);
        } catch (e) {
            toast.error("Activation échouée");
        } finally {
            setActivating(null);
        }
    };

    const filtered = voices.filter((v) => {
        if (filter === "all") return true;
        if (filter === "custom") return !!v.is_custom;
        if (filter === "male") return v.gender === "male";
        if (filter === "female") return v.gender === "female";
        if (filter === "educational")
            return ["informative_educational", "narrative_story", "conversational"].includes(v.use_case);
        return true;
    });

    if (loading) {
        return (
            <div className="glass-card rounded-3xl p-6 sm:p-8 animate-pulse">
                <div className="h-6 w-48 bg-white/10 rounded mb-4" />
                <div className="space-y-3">
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="h-16 bg-white/5 rounded-2xl" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-3xl p-6 sm:p-8"
            data-testid="tts-voice-settings"
        >
            <div className="flex items-start gap-3 mb-6">
                <div className="w-11 h-11 rounded-2xl bg-shariti-orange/15 flex items-center justify-center shrink-0">
                    <Mic className="w-5 h-5 text-shariti-orange-light" />
                </div>
                <div className="flex-1">
                    <h3 className="text-lg font-extrabold text-white tracking-tight">
                        Voix du formateur Shariti
                    </h3>
                    <p className="text-sm text-white/55 font-light leading-relaxed mt-1">
                        Choisis la voix française qui lit les leçons en Mode Présentation. Powered
                        by ElevenLabs — qualité broadcast. <strong className="text-white/75">Le simulateur IA garde sa propre voix anglaise</strong> (différenciation formateur vs avatar de roleplay).
                    </p>
                </div>
            </div>

            {/* Filtres */}
            <div className="flex flex-wrap gap-2 mb-5">
                {[
                    { id: "all", label: "Toutes" },
                    { id: "custom", label: "✦ Mes voix" },
                    { id: "male", label: "Masculin" },
                    { id: "female", label: "Féminin" },
                    { id: "educational", label: "Pédagogique" },
                ].map((f) => (
                    <button
                        key={f.id}
                        onClick={() => setFilter(f.id)}
                        data-testid={`tts-filter-${f.id}`}
                        className={`text-[11px] font-bold tracking-wide px-3 py-1.5 rounded-full transition-all border ${
                            filter === f.id
                                ? "bg-shariti-orange text-white border-shariti-orange"
                                : "bg-white/3 text-white/55 border-white/10 hover:text-white"
                        }`}
                    >
                        {f.label}
                    </button>
                ))}
                <span className="ml-auto text-[11px] text-white/40 font-light self-center">
                    {filtered.length} voix · active : <span className="text-shariti-orange-light font-bold">{voices.find((v) => v.voice_id === activeId)?.name || "—"}</span>
                </span>
            </div>

            {/* Liste des voix */}
            <div className="space-y-2.5 max-h-[600px] overflow-y-auto pr-1">
                {filtered.map((v) => {
                    const isActive = v.voice_id === activeId;
                    const isPreviewing = previewing === v.voice_id;
                    const isPlaying = playing === v.voice_id;
                    return (
                        <div
                            key={v.voice_id}
                            data-testid={`tts-voice-${v.voice_id}`}
                            className={`rounded-2xl p-3.5 flex items-center gap-3 transition-all border ${
                                isActive
                                    ? "bg-shariti-orange/10 border-shariti-orange/50 orange-glow"
                                    : "bg-white/3 border-white/8 hover:border-white/20"
                            }`}
                        >
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <p className="text-sm font-extrabold text-white truncate">
                                        {v.name}
                                    </p>
                                    {v.is_custom && (
                                        <span
                                            className="text-[9px] font-extrabold tracking-[0.18em] uppercase bg-emerald-500/20 text-emerald-200 px-2 py-0.5 rounded-full"
                                            title="Voix clonée / personnalisée de ton compte ElevenLabs"
                                        >
                                            ✦ Custom
                                        </span>
                                    )}
                                    {isActive && (
                                        <span className="text-[9px] font-extrabold tracking-[0.18em] uppercase bg-shariti-orange/20 text-shariti-orange-light px-2 py-0.5 rounded-full">
                                            Active
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 mt-1 flex-wrap text-[10px] font-light text-white/45">
                                    {v.gender && <span>{v.gender === "male" ? "♂" : v.gender === "female" ? "♀" : v.gender}</span>}
                                    {v.accent && <span>· {v.accent}</span>}
                                    {v.descriptive && <span>· {v.descriptive}</span>}
                                    {v.use_case && <span>· {v.use_case.replace(/_/g, " ")}</span>}
                                </div>
                            </div>
                            <button
                                onClick={() => handlePreview(v.voice_id)}
                                disabled={isPreviewing}
                                data-testid={`tts-preview-${v.voice_id}`}
                                title={isPlaying ? "Stop" : "Écouter un échantillon"}
                                className="w-9 h-9 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/12 text-white/75 hover:text-white transition-all disabled:opacity-40"
                            >
                                {isPreviewing ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : isPlaying ? (
                                    <Pause className="w-4 h-4" />
                                ) : (
                                    <Play className="w-4 h-4 ml-0.5" />
                                )}
                            </button>
                            <button
                                onClick={() => handleActivate(v.voice_id, v.name)}
                                disabled={isActive || activating === v.voice_id}
                                data-testid={`tts-activate-${v.voice_id}`}
                                className={`text-[11px] font-bold px-3 py-2 rounded-full transition-all whitespace-nowrap ${
                                    isActive
                                        ? "bg-shariti-orange/10 text-shariti-orange-light cursor-default"
                                        : "bg-shariti-orange/15 hover:bg-shariti-orange text-shariti-orange-light hover:text-white border border-shariti-orange/30"
                                }`}
                            >
                                {activating === v.voice_id ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : isActive ? (
                                    <Check className="w-3.5 h-3.5" />
                                ) : (
                                    "Activer"
                                )}
                            </button>
                        </div>
                    );
                })}
                {filtered.length === 0 && (
                    <p className="text-center text-white/40 text-sm font-light py-8">
                        Aucune voix ne correspond au filtre actuel.
                    </p>
                )}
            </div>

            {/* Tip */}
            <div className="mt-5 px-3 py-2.5 rounded-xl bg-shariti-orange/5 border border-shariti-orange/20 text-[11px] text-shariti-orange-light/85 font-light leading-relaxed flex items-start gap-2">
                <Volume2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <p>
                    <strong className="font-extrabold">Astuce</strong> : pour le profil "formateur Shariti dynamique terrain", les voix de catégorie <span className="font-bold">Pédagogique</span> (E-Learning, Training, Documentary) avec accent <span className="font-bold">standard</span> ou <span className="font-bold">parisian</span> donnent les meilleurs résultats. Évite "Quebec" (accent canadien) et "Meridional" (sud).
                </p>
            </div>
        </motion.div>
    );
}
